import React, { useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useTheme } from '@mui/material/styles';
import { format } from 'date-fns';
import { Box, FormControl, InputLabel, Select, MenuItem } from '@mui/material';

const RegionalBreakdownChart = ({ data }) => {
  const theme = useTheme();
  const [selectedMonth, setSelectedMonth] = useState('');
  
  // Process the data for the chart
  const { chartData, availableMonths } = useMemo(() => {
    if (!data || data.length === 0) return { chartData: [], availableMonths: [] };
    
    // Get unique months from data
    const months = [...new Set(data.map(item => item.month))].sort();
    
    // Default to most recent month if none selected
    const monthToUse = selectedMonth || months[months.length - 1];
    
    // Filter data for selected month and group by region and energy source
    const filteredData = data.filter(item => item.month === monthToUse);
    
    // Group by region
    const groupedByRegion = filteredData.reduce((acc, item) => {
      const region = item.region;
      if (!acc[region]) {
        acc[region] = {
          region,
          solar: 0,
          wind: 0,
          hydro: 0,
          fossil: 0,
          total: 0
        };
      }
      
      acc[region][item.energy_source] = item.profit;
      acc[region].total += item.profit;
      
      return acc;
    }, {});
    
    // Convert to array and sort by total profit
    const processedData = Object.values(groupedByRegion).sort((a, b) => b.total - a.total);
    
    return { 
      chartData: processedData,
      availableMonths: months
    };
  }, [data, selectedMonth]);
  
  // Handle month selection change
  const handleMonthChange = (event) => {
    setSelectedMonth(event.target.value);
  };
  
  // Custom tooltip formatter
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ 
          backgroundColor: '#fff', 
          padding: '10px', 
          border: '1px solid #ccc',
          borderRadius: '4px'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ margin: '5px 0', color: entry.color }}>
              {entry.name}: ${entry.value.toLocaleString()}
            </p>
          ))}
          <p style={{ margin: '5px 0', fontWeight: 'bold' }}>
            Total Profit: ${payload.reduce((sum, entry) => sum + entry.value, 0).toLocaleString()}
          </p>
        </div>
      );
    }
    return null;
  };

  // Format month for display
  const formatMonth = (month) => {
    if (!month) return '';
    const [year, monthNum] = month.split('-');
    return format(new Date(parseInt(year), parseInt(monthNum) - 1), 'MMMM yyyy');
  };

  return (
    <Box>
      <Box sx={{ mb: 2, width: 250 }}>
        <FormControl fullWidth>
          <InputLabel id="month-select-label">Month</InputLabel>
          <Select
            labelId="month-select-label"
            id="month-select"
            value={selectedMonth || (availableMonths.length > 0 ? availableMonths[availableMonths.length - 1] : '')}
            label="Month"
            onChange={handleMonthChange}
          >
            {availableMonths.map(month => (
              <MenuItem key={month} value={month}>
                {formatMonth(month)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="region" />
          <YAxis 
            tickFormatter={(value) => `$${value / 1000}k`}
            label={{ value: 'Profit ($)', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Bar 
            dataKey="solar" 
            name="Solar" 
            stackId="a" 
            fill={theme.palette.energy.solar} 
          />
          <Bar 
            dataKey="wind" 
            name="Wind" 
            stackId="a" 
            fill={theme.palette.energy.wind} 
          />
          <Bar 
            dataKey="hydro" 
            name="Hydro" 
            stackId="a" 
            fill={theme.palette.energy.hydro} 
          />
          <Bar 
            dataKey="fossil" 
            name="Fossil" 
            stackId="a" 
            fill={theme.palette.energy.fossil} 
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default RegionalBreakdownChart;
